import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

function formatAxisDate(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function ChartTooltip({ active, payload, label, commitsLabel, linesLabel }) {
  if (!active || !payload?.length) return null;
  const commits = payload.find((p) => p.dataKey === 'commits')?.value ?? 0;
  const lines = payload.find((p) => p.dataKey === 'lines_added')?.value ?? 0;
  return (
    <div className="rounded-sm border border-zinc-200 bg-white px-3 py-2 text-xs shadow-md">
      <p className="font-semibold text-zinc-800 mb-1">{formatAxisDate(label)}</p>
      <p className="text-orange-600 font-mono tabular-nums">{commitsLabel}: {commits}</p>
      <p className="text-[#0055ff] font-mono tabular-nums">{linesLabel}: {lines.toLocaleString()}</p>
    </div>
  );
}

export default function ActivityTimelineChart({
  points = [],
  commitsLabel,
  linesLabel,
  loading = false,
  emptyLabel,
}) {
  const hasData = points.some((p) => p.commits > 0 || p.lines_added > 0);

  if (loading) {
    return (
      <div className="h-64 flex items-center justify-center text-zinc-500 text-sm" data-testid="activity-chart-loading">
        …
      </div>
    );
  }

  if (!hasData) {
    return (
      <div className="h-64 flex items-center justify-center text-zinc-500 text-sm" data-testid="activity-chart-empty">
        {emptyLabel}
      </div>
    );
  }

  return (
    <div className="h-64 w-full" data-testid="activity-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={points} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={formatAxisDate}
            tick={{ fontSize: 10, fill: '#71717a' }}
            tickLine={false}
            axisLine={{ stroke: '#e4e4e7' }}
            minTickGap={24}
          />
          <YAxis
            yAxisId="lines"
            tick={{ fontSize: 10, fill: '#71717a' }}
            tickLine={false}
            axisLine={false}
            width={48}
            tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)}
          />
          <YAxis
            yAxisId="commits"
            orientation="right"
            tick={{ fontSize: 10, fill: '#71717a' }}
            tickLine={false}
            axisLine={false}
            width={32}
            allowDecimals={false}
          />
          <Tooltip
            content={<ChartTooltip commitsLabel={commitsLabel} linesLabel={linesLabel} />}
          />
          <Bar
            yAxisId="lines"
            dataKey="lines_added"
            fill="#0055ff"
            fillOpacity={0.35}
            stroke="#0055ff"
            strokeWidth={1}
            radius={[2, 2, 0, 0]}
            maxBarSize={28}
          />
          <Line
            yAxisId="commits"
            type="monotone"
            dataKey="commits"
            stroke="#ea580c"
            strokeWidth={2}
            dot={{ r: 2, fill: '#ea580c', strokeWidth: 0 }}
            activeDot={{ r: 4 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
